import type { ReaderName } from "@bindery/shared/contracts/session";
import { Alert, AlertDescription } from "@bindery/ui/components/ui/alert";
import { Button } from "@bindery/ui/components/ui/button";
import { Input } from "@bindery/ui/components/ui/input";
import { Label } from "@bindery/ui/components/ui/label";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { SignedIn } from "../chrome/signed-in";
import { Workbench } from "../chrome/workbench";
import { useBoundReader } from "../data/use-bound-reader";
import { useCreateWorksheet, usePrintName } from "../data/use-worksheets";
import type { SheetHandoff } from "../lib/sheet-handoff";
import { ROUTES, worksheetPath } from "../routes";
import { readerDisplayName } from "../lib/reader-name";

const PRINT_NAME_MAX_LENGTH = 40;

function CreateForm({ reader }: { reader: ReaderName }) {
  const create = useCreateWorksheet(reader.readerId);
  const navigate = useNavigate();
  const [printName, setPrintName] = usePrintName(readerDisplayName(reader));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFailed(false);
    create().then(
      ({ worksheetId, gradeToken }) => {
        const handoff: SheetHandoff = { gradeToken, printName };
        void navigate(worksheetPath(worksheetId), { state: handoff });
      },
      () => {
        setBusy(false);
        setFailed(true);
      },
    );
  };

  return (
    <form onSubmit={submit} className="flex max-w-md flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Label htmlFor="print-name">Name on the sheet</Label>
        <Input
          id="print-name"
          value={printName}
          maxLength={PRINT_NAME_MAX_LENGTH}
          autoComplete="off"
          onChange={(event) => setPrintName(event.target.value)}
        />
        <p className="text-muted-foreground text-sm">
          Printed in the sheet’s corner and nowhere else; leave it empty for a
          line to write on.
        </p>
      </div>
      {failed ? (
        <Alert variant="destructive">
          <AlertDescription>
            The worksheet wasn’t made. Check the connection and try again.
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={busy}>
          {busy ? "Making the worksheet" : "Make the worksheet"}
        </Button>
        <Button variant="ghost" asChild>
          <Link to={ROUTES.home}>Back</Link>
        </Button>
      </div>
    </form>
  );
}

function CreateContent() {
  const { boundReader } = useBoundReader();
  if (boundReader === null) return <Navigate to={ROUTES.home} replace />;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="font-serif text-3xl font-semibold tracking-tight">
          A worksheet for {readerDisplayName(boundReader)}
        </h1>
        <p className="text-muted-foreground max-w-prose">
          Fifteen first sentences, each with one word to find among look-alikes.
          Print it, sit with the reader, then scan the corner to record how it
          went.
        </p>
      </div>
      <CreateForm reader={boundReader} />
    </div>
  );
}

export function CreatePage() {
  return (
    <Workbench>
      <SignedIn returnTo={ROUTES.create}>{() => <CreateContent />}</SignedIn>
    </Workbench>
  );
}
